/**
 * EVFlow Executor
 * Executes single-symbol trades based on EVFlow signals with chase orders and ATR-based SL/TP
 *
 * Uses ChaseUtils for shared functionality (consolidated patterns from multiple executors)
 */

class EVFlowExecutor {
    constructor() {
        this.activeExecutions = new Map();
        this.CHASE_TIMEOUT_MS = 60000; // 60 seconds max chase time
        this.CHASE_SETTINGS = {
            tickDistance: 5,       // 5 ticks from mid
            frequencyRangeMin: 5,  // Check every 5 seconds
            frequencyRangeMax: 5,  // Fixed 5 second intervals
            useAnchor: false,
            tpslEnabled: false,
            aggressive: true       // Cross spread: +5 above mid for long, -5 below mid for short
        };
        this.INITIAL_PERCENT_OFFSET = 0; // Start at market price
        this.ATR_MULTIPLIER = 1.5; // 1.5x ATR for both TP and SL (symmetric 1:1)
    }

    // Helper to get ChaseUtils (may not be loaded immediately)
    get utils() {
        return window.ChaseUtils;
    }

    /**
     * Execute an EVFlow trade with chase limit order
     * @param {Object} params - Trade parameters
     * @param {string} params.symbol - Symbol to trade (e.g., "ETH")
     * @param {string} params.direction - Direction ("LONG" or "SHORT")
     * @param {number} params.sizeUSD - Trade size in USD
     * @param {boolean} params.autoSlTp - Whether to place SL/TP orders
     * @returns {Promise<Object>} Execution result
     */
    async executeEVFlowTrade(params) {
        const { symbol, direction, sizeUSD, autoSlTp = true } = params;
        const executionId = `evflow_${Date.now()}`;

        // Validate inputs
        if (!symbol || !direction || sizeUSD <= 0) {
            throw new Error('Invalid parameters: symbol, direction, and positive sizeUSD required');
        }

        console.log(`[EVFLOW-EXECUTOR] Starting ${executionId}: ${direction} ${symbol} $${sizeUSD}`);

        try {
            // Pre-fetch ATR if autoSlTp is enabled (ensures it's cached)
            if (autoSlTp && window.ATRService?.getATR) {
                await window.ATRService.getATR(symbol);
            }

            // Get current price
            const price = await this.getPrice(symbol);
            if (!price) {
                throw new Error(`Failed to get price for ${symbol}`);
            }

            // Calculate size in asset units
            const size = sizeUSD / price;

            // Store execution state
            this.activeExecutions.set(executionId, {
                symbol,
                direction,
                size,
                sizeUSD,
                price,
                startTime: Date.now(),
                status: 'EXECUTING',
                currentOid: null,
                filled: 0
            });

            console.log(`[EVFLOW-EXECUTOR] Calculated size: ${symbol}=${size.toFixed(6)} @ $${price}`);

            // Place order
            const result = await this.placeLegOrder(symbol, direction, size, price);

            if (!result.success) {
                throw new Error(`Failed to place order: ${result.error}`);
            }

            // Store order ID from SDK response (authoritative source)
            const execution = this.activeExecutions.get(executionId);
            execution.currentOid = result.orderId;
            execution.limitPrice = result.limitPrice;

            console.log(`[EVFLOW-EXECUTOR] Order placed successfully:`, {
                oid: result.orderId,
                symbol,
                direction,
                size,
                limitPrice: result.limitPrice
            });

            // Start chase immediately - delta monitoring in ChaseTracker handles fill detection
            // No need for waitForOrderConfirmation or instant-fill workaround anymore
            console.log(`[EVFLOW-EXECUTOR] Starting chase for ${symbol}:`, { oid: result.orderId, price: result.limitPrice });
            await this.startChase(result.orderId, symbol, direction, size, result.limitPrice);

            // Monitor until filled or timeout
            const execResult = await this.monitorExecution(executionId, symbol, direction, size);

            // Place auto SL/TP orders after fill - only if position verified
            if (execResult.status === 'COMPLETED' && autoSlTp) {
                console.log(`[EVFLOW-EXECUTOR] Placing SL/TP orders for ${symbol}...`);
                await this.placeSlTp({
                    symbol,
                    direction,
                    size: execResult.filled || size
                });
            } else if (execResult.status === 'FAILED') {
                console.warn(`[EVFLOW-EXECUTOR] Execution FAILED - no SL/TP placed. Error: ${execResult.error}`);
            }

            return execResult;

        } catch (error) {
            console.error(`[EVFLOW-EXECUTOR] Error:`, error);
            this.activeExecutions.delete(executionId);
            throw error;
        }
    }

    async getPrice(symbol) {
        // Delegate to ChaseUtils for consistent price fetching (handles HIP-3)
        if (!this.utils) {
            console.error('[EVFLOW-EXECUTOR] ChaseUtils not loaded');
            return null;
        }
        return this.utils.getPrice(symbol);
    }

    async placeLegOrder(symbol, direction, size, price) {
        // Delegate to ChaseUtils for consistent order placement
        const result = await this.utils?.placeLimitOrder(symbol, direction, size, price, this.INITIAL_PERCENT_OFFSET);
        if (result) {
            console.log(`[EVFLOW-EXECUTOR] Order placed via ChaseUtils: oid=${result.orderId}`);
        }
        return result || { success: false, error: 'ChaseUtils not available' };
    }

    async startChase(orderId, symbol, direction, size, price) {
        // Delegate to ChaseUtils for consistent chase initialization
        await this.utils?.startChase(orderId, symbol, direction, size, price, this.CHASE_SETTINGS);
    }

    async monitorExecution(executionId, symbol, direction, size, timeoutMs = 60000) {
        const startTime = Date.now();
        const execution = this.activeExecutions.get(executionId);

        // Validate size to prevent division by zero
        if (!size || size <= 0) {
            console.error(`[EVFLOW-EXECUTOR] Invalid size (${size}), cannot monitor`);
            if (execution) {
                execution.status = 'FAILED';
                execution.error = 'Invalid size';
                this.activeExecutions.delete(executionId);
            }
            return execution;
        }

        console.log(`[EVFLOW-EXECUTOR] Monitoring execution ${executionId}: ${symbol} ${direction} size=${size.toFixed(6)}`);

        // Simple monitoring: check if chase is still active
        // ChaseTracker now handles fill detection via delta monitoring (500ms polling)
        while (Date.now() - startTime < timeoutMs) {
            // Check if chase is still active for this symbol
            const chase = window.BrowserChaseTracker?.getChaseForOrder({
                coin: symbol,
                side: direction === 'LONG' ? 'B' : 'A',
                sz: size
            });

            if (!chase) {
                // Chase stopped - check if it was due to fill
                // Use delta tracker to verify fill status
                if (window.PositionDeltaTracker) {
                    // Find the execution's chase ID (stored when chase started)
                    const chases = window.BrowserChaseTracker?.chaseHistory || [];
                    const completedChase = chases.find(c =>
                        c.asset === symbol &&
                        c.endReason === 'order_filled'
                    );

                    if (completedChase) {
                        execution.filled = size;
                        execution.status = 'COMPLETED';
                        console.log(`[EVFLOW-EXECUTOR] Chase completed with fill - execution COMPLETED`);
                        break;
                    }
                }

                // Fallback: verify position exists
                const positionExists = await this.verifyPositionExists(symbol, direction);
                if (positionExists) {
                    execution.filled = size;
                    execution.status = 'COMPLETED';
                    console.log(`[EVFLOW-EXECUTOR] Position verified - execution COMPLETED`);
                    break;
                } else {
                    // Chase stopped but no position - might have been cancelled
                    execution.status = 'FAILED';
                    execution.error = 'Chase stopped without creating position';
                    console.warn(`[EVFLOW-EXECUTOR] Chase stopped but NO POSITION for ${symbol}`);
                    break;
                }
            }

            // Chase still active - wait and check again
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Timeout handling
        if (execution.status !== 'COMPLETED' && execution.status !== 'FAILED') {
            // Check if we have a position despite timeout
            const positionExists = await this.verifyPositionExists(symbol, direction);
            if (positionExists) {
                execution.filled = size;
                execution.status = 'COMPLETED';
                console.log(`[EVFLOW-EXECUTOR] Timeout but position exists - marking COMPLETED`);
            } else {
                execution.status = 'TIMEOUT';
                console.warn(`[EVFLOW-EXECUTOR] Execution timeout - no position found for ${symbol}`);
            }
        }

        this.activeExecutions.delete(executionId);
        return execution;
    }

    /**
     * Verify that a position exists for the given symbol and direction
     * Delegates to ChaseUtils for consistent verification (handles HIP-3)
     */
    async verifyPositionExists(symbol, direction) {
        return this.utils?.verifyPositionExists(symbol, direction) || false;
    }

    /**
     * Place SL/TP orders using ATR
     * Uses ChaseUtils for position entry and ATR distance
     */
    async placeSlTp({ symbol, direction, size }) {
        // Wait for position to appear using ChaseUtils
        const posData = await this.utils?.getPositionEntry(symbol);
        if (!posData || !posData.size) {
            console.error(`[EVFLOW-EXECUTOR] Cannot place SL/TP - position not found`);
            return [];
        }
        const entryPrice = posData.entryPrice;
        // Use ACTUAL position size from exchange (properly rounded) instead of passed-in size
        const actualSize = posData.size;

        // Get ATR distance using ChaseUtils (handles 2% fallback)
        const atrResult = await this.utils?.getATRDistance(symbol, entryPrice, this.ATR_MULTIPLIER);
        const atrDistance = atrResult?.distance || (entryPrice * 0.02 * this.ATR_MULTIPLIER);
        const isFallback = atrResult?.isFallback || false;

        if (isFallback) {
            console.warn(`[EVFLOW-EXECUTOR] Using ATR fallback for ${symbol}`);
        } else {
            console.log(`[EVFLOW-EXECUTOR] Using ATR: ${atrResult?.atr?.toFixed(4)} × ${this.ATR_MULTIPLIER} = ${atrDistance.toFixed(4)}`);
        }

        const tpPrice = direction === 'LONG' ? entryPrice + atrDistance : entryPrice - atrDistance;
        const slPrice = direction === 'LONG' ? entryPrice - atrDistance : entryPrice + atrDistance;

        console.log(`[EVFLOW-EXECUTOR] Placing SL/TP:`, {
            symbol,
            entry: entryPrice,
            size: actualSize,
            tp: tpPrice.toFixed(4),
            sl: slPrice.toFixed(4),
            atr: atrResult?.atr?.toFixed(4) || 'fallback',
            mult: this.ATR_MULTIPLIER,
            isFallback
        });

        // Use actualSize from exchange (properly rounded) to avoid "invalid size" errors
        const orders = await Promise.allSettled([
            // TP order
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbol,
                orderSide: direction === 'LONG' ? 'buy' : 'sell',
                size: actualSize,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: tpPrice,
                slPrice: null
            }),
            // SL order
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbol,
                orderSide: direction === 'LONG' ? 'buy' : 'sell',
                size: actualSize,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: null,
                slPrice: slPrice
            })
        ]);

        const results = orders.map((o, i) => ({
            order: i === 0 ? 'TP' : 'SL',
            status: o.status,
            value: o.value?.success,
            error: o.reason?.message || o.value?.error
        }));
        console.log(`[EVFLOW-EXECUTOR] SL/TP results:`, results);

        return results;
    }

    cancelExecution(executionId) {
        const execution = this.activeExecutions.get(executionId);
        if (execution) {
            execution.status = 'CANCELLED';
            if (execution.currentOid && window.BrowserHyperliquidClient) {
                window.BrowserHyperliquidClient.cancelOrder({
                    orderId: execution.currentOid,
                    asset: execution.symbol
                }).catch(console.error);
            }
            this.activeExecutions.delete(executionId);
        }
    }

    getActiveExecutions() {
        return Array.from(this.activeExecutions.entries()).map(([id, exec]) => ({
            executionId: id,
            ...exec
        }));
    }
}

// Export to window for V4 browser services
if (typeof window !== 'undefined') {
    window.EVFlowExecutor = new EVFlowExecutor();
    console.log('[EVFLOW-EXECUTOR] Loaded and ready');
}
